if (!customElements.get('splide-component')) {
  customElements.define(
    'splide-component',
    class SplideComponent extends HTMLElement {
      constructor() {
        super();
        this.sliderContainer = this.querySelector('.splide');

        // Listen for Shopify section load event
        document.addEventListener('shopify:section:load', (event) => {
          this.initializeSlider();
        });
      }

      connectedCallback() {
        document.addEventListener('DOMContentLoaded', () => this.initializeSlider());
      }

      initializeSlider() {
        // Dataset configurations
        this.videoSlider = this.dataset.videoSlider;
        this.sliderType = this.dataset.type || 'slide';
        this.sliderTypeMobile = this.dataset.typeMobile || this.sliderType;
        this.sliderDirection = this.dataset.direction || 'ltr';
        this.rewindSlider = this.sliderType === 'fade';
        this.autoPlayEnabled = this.dataset.autoplay === 'true';
        this.autoPlaySpeed =
          this.dataset.autoplaySpeed && !isNaN(parseInt(this.dataset.autoplaySpeed))
            ? 1000 * parseInt(this.dataset.autoplaySpeed)
            : 5000;
        this.dragMode = this.dataset.drag !== 'false' && (this.dataset.drag !== 'free' || 'free');
        this.focusSlide = this.dataset.focus || 0;
        this.trimSpaceEnabled = this.dataset.trimSpace !== 'false';
        this.arrowsEnabled = this.dataset.arrows !== 'false';
        this.paginationEnabled = this.dataset.pagination !== 'false';
        this.omitEndEnabled = true;
        this.slidesPerPageDesktop =
          this.dataset.slidesDesktop && !isNaN(parseInt(this.dataset.slidesDesktop))
            ? parseInt(this.dataset.slidesDesktop)
            : 3;
        this.autoWidthEnabled = this.dataset.autoWidthEnabled;
        this.slidesPerPageMobile =
          this.dataset.slidesMobile && !isNaN(parseInt(this.dataset.slidesMobile))
            ? parseInt(this.dataset.slidesMobile)
            : 1;
        this.slidesPerMoveDesktop =
          this.dataset.perMoveDesktop && !isNaN(parseInt(this.dataset.perMoveDesktop))
            ? parseInt(this.dataset.perMoveDesktop)
            : 1;
        this.slidesPerMoveMobile =
          this.dataset.perMoveMobile && !isNaN(parseInt(this.dataset.perMoveMobile))
            ? parseInt(this.dataset.perMoveMobile)
            : 1;
        this.gapBetweenSlidesDesktop = this.dataset.gapDesktop ? parseInt(this.dataset.gapDesktop) : 30;
        this.gapBetweenSlidesMobile = this.dataset.gapMobile ? parseInt(this.dataset.gapMobile) : 15;
        this.paddingCalculationDesktop = this.dataset.paddingCalcDesktop === 'true';
        this.rightPaddingDesktop = parseInt(this.dataset.sidePaddingDesktop) || 0;
        this.leftPaddingDesktop = this.paddingCalculationDesktop ? 0 : this.rightPaddingDesktop;
        this.paddingCalculationMobile = this.dataset.paddingCalcMobile === 'true';
        this.rightPaddingMobile = parseInt(this.dataset.sidePaddingMobile) || 0;
        this.leftPaddingMobile = this.paddingCalculationMobile ? 0 : this.rightPaddingMobile;
        this.destroySliderDesktop = this.dataset.destroyDesktop === 'true';
        this.destroySliderMobile = this.dataset.destroyMobile === 'true';

        // Slider configuration
        this.sliderConfig = {
          type: this.sliderType,
          direction: this.sliderDirection,
          rewind: this.rewindSlider,
          autoplay: this.autoPlayEnabled,
          interval: this.autoPlaySpeed,
          drag: this.dragMode,
          focus: this.focusSlide,
          trimSpace: this.trimSpaceEnabled,
          arrows: this.arrowsEnabled,
          pagination: this.paginationEnabled,
          omitEnd: this.omitEndEnabled,
          perPage: this.slidesPerPageDesktop,
          perMove: this.slidesPerMoveDesktop,
          autoWidth: this.autoWidthEnabled,
          gap: this.gapBetweenSlidesDesktop,
          padding: {
            left: this.leftPaddingDesktop,
            right: this.rightPaddingDesktop,
          },
          destroy: this.destroySliderDesktop,
          classes: {
            arrow: `splide__arrow slider-button`,
            page: `splide__pagination__page`,
          },
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
          breakpoints: {
            749: {
              omitEnd: this.omitEndEnabled,
              type: this.sliderTypeMobile,
              perPage: this.slidesPerPageMobile,
              perMove: this.slidesPerMoveMobile,
              gap: this.gapBetweenSlidesMobile,
              padding: {
                left: this.leftPaddingMobile,
                right: this.rightPaddingMobile,
              },
              destroy: this.destroySliderMobile,
            },
          },
        };

        // Initialize Splide slider
        const splideInstance = new Splide(this.sliderContainer, this.sliderConfig);

        // Event: Slider mounted
        splideInstance.on('mounted', () => {
          const currentIndex = splideInstance.index;
          const currentSlide = splideInstance.Components.Elements.slides[currentIndex];

          if (currentSlide) {
            const slideImage = currentSlide.querySelector('img');
            if (slideImage) {
              if (slideImage.complete) {
                currentSlide.classList.add('is-instant-active');
                this.setActiveSlideHeight(currentSlide);
              } else {
                slideImage.addEventListener('load', () => {
                  currentSlide.classList.add('is-instant-active');
                  this.setActiveSlideHeight(currentSlide);
                });
              }
            } else {
              currentSlide.classList.add('is-instant-active');
              this.setActiveSlideHeight(currentSlide);
            }
          }
        });

        // Event: Slide moved
        splideInstance.on('move', () => {
          const currentIndex = splideInstance.index;
          const slides = splideInstance.Components.Elements.slides;

          if (slides) {
            const currentSlide = slides[currentIndex];
            if (this.dataset.videoAutoplay === 'false' && this.dataset.videoSlider == 'true') {
              slides.forEach((slide, idx) => {
                const video = slide.querySelector('video');
                const playButton = slide.querySelector('.video-play_button');
                if (!video) return;

                if (idx === currentIndex) {
                  video.currentTime = 0;
                  video.play();
                  if (playButton) playButton.classList.add('hidden');
                } else {
                  video.pause();
                  if (playButton) playButton.classList.remove('hidden');
                }
              });
            }

            if (currentSlide) {
              slides.forEach((slide) => slide.classList.remove('is-instant-active'));
              currentSlide.classList.add('is-instant-active');
              this.setActiveSlideHeight(currentSlide);
            }
          }
        });

        // Event: Pause videos on hidden slides
        if (this.dataset.pauseVideos === 'true') {
          splideInstance.on('hidden', (event) => {
            const hiddenSlide = event.slide;
            const videoElement = hiddenSlide.querySelector('internal-video');

            if (videoElement) {
              const actionOnInactive = videoElement.dataset.actionOnInactive;
              if (actionOnInactive === 'pause') {
                videoElement.video.pause();
                videoElement.classList.remove('internal-video--playing');
              } else if (actionOnInactive === 'mute') {
                videoElement.video.muted = true;
                videoElement.classList.add('internal-video--muted');
              }
            }
          });
        }

        splideInstance.mount();
        if (this.dataset.videoAutoplay !== 'true' && this.dataset.videoSlider == 'true') {
          const videos = this.querySelectorAll('video');

          videos.forEach((video) => {
            const videoWrapper = video.closest('.video_slider');
            const playIcon = video.nextElementSibling;

            if (!videoWrapper) return;

            video.addEventListener('ended', () => {
              if (playIcon) playIcon.classList.remove('hidden');
            });

            videoWrapper.addEventListener('click', (e) => {
              e.preventDefault();

              if (video.paused) {
                video.play();
                if (playIcon) playIcon.classList.add('hidden');

                videos.forEach((otherVideo) => {
                  if (otherVideo !== video) {
                    otherVideo.pause();
                    const otherIcon = otherVideo.nextElementSibling;
                    if (otherIcon) otherIcon.classList.remove('hidden');
                  }
                });
              } else {
                video.pause();
                if (playIcon) playIcon.classList.remove('hidden');
              }
            });
          });
        }
      }

      setActiveSlideHeight(slide) {
        if (slide) {
          const slideHeight = slide.offsetHeight;
          this.style.setProperty('--active-slide-height', `${slideHeight}px`);
        }
      }
    }
  );
}
